// ResourceHead is not actually a molecule as there are hard dependency on status update
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Approval } from 'api/approval';
import { TaskStatusType } from 'api/status-updates';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { ProgressPie } from 'components/atoms/progress-pie';
import { showConfirmPopup } from 'components/atoms/show-confirm-popup';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { ResourceListSectionConfig } from 'components/molecules/resource-list';
import {
  DocumentResourceType,
  EditorModes,
  ResourceSectionType,
  selectActiveDocumentInfo,
  selectDocumentResourceSections,
  updateDocResource,
} from 'data/documents';
import {
  isTaskApprovalMatchesFilter,
  isTaskMatchesFilter,
  selectActiveStatusUpdate,
  selectActiveStatusUpdateHighlightTasks,
} from 'data/status-updates';
import { useCKEditor } from 'hooks/use-ck-editor';
import { useIsResponsive } from 'hooks/use-media-query';
import { useTranslation } from 'hooks/use-translation';
// features here.
import { StatusField } from 'stores/fields';
import { executeUpdateResourceSectionCommand } from '../editor-commands';
import {
  Container,
  MilestoneIcon,
  RangeInfo,
  ResourceText,
  TitleMain,
  StatusIcon,
  Title,
} from './styles';

const getResourceType = (type: ResourceSectionType) => {
  switch (type) {
    case ResourceSectionType.Task:
      return 'tasks';
    case ResourceSectionType.Milestone:
      return 'milestones';
    case ResourceSectionType.Approval:
      return 'approvals';
    default:
      return 'tasks';
  }
};

interface Props {
  config: ResourceListSectionConfig;
  statusField: StatusField;
}

export const ResourceHead: FC<Props> = ({ config, statusField }) => {
  const {
    resourceSectionId,
    resourceIds,
    resourceSectionType,
    resourceSectionFilterType,
    resourceSectionFilterRange,
    originalResourceIds,
  } = config;

  const updateConfirmationMessageTitle = useTranslation(
    'status_updates.refresh_entries_title',
  );
  const updateConfirmationMessageContent = useTranslation(
    'status_updates.refresh_entries_description',
  );
  const translatedYesText = useTranslation('update');
  const translatedNoText = useTranslation('cancel');
  const editor = useCKEditor();

  const [updatedResourceIds, setUpdatedResources] = useState<number[]>([]);
  const allHighlightTasks = useSelector(selectActiveStatusUpdateHighlightTasks);
  const highlightTasks = allHighlightTasks[resourceSectionType];
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const documentResources = useSelector(selectDocumentResourceSections);
  const activeDocumentDetails = useSelector(selectActiveDocumentInfo);
  const dispatch = useDispatch();
  // We need to update resourceIds with only the new values.
  // ex: originalResourceIds were [1,2,3,4,5]
  // user deletes 4 and current resourceIds are [1,2,4,5]
  // and the tasks changed after sometime, ie, updatedResourceIds are [1,2,3,4,5,6]
  // now the logic should give back [1,2,4,5,6]
  // and if updatedResourceIds are [1,3,4,5,6], we still give back [1,2,4,5,6] and user can
  // give back remove 3 herself.

  const onUpdateEntries = () => {
    setUpdatedResources([]);

    const payload = activeStatusUpdate
      ? {
          projectID: activeStatusUpdate?.project.projectId,
          documentID: activeStatusUpdate?.document.documentId,
          documentSectionId: activeDocumentDetails.publicSection.id,
          sectionType: EditorModes.Public,
          resourceSections: uniqBy(
            [
              {
                resourceSectionId,
                resourceIds:
                  uniq([
                    ...resourceIds,
                    ...difference(updatedResourceIds, originalResourceIds),
                  ]) || [],
                resourceSectionType: ResourceSectionType.Task,
                resourceType: DocumentResourceType.Default,
              },
              ...documentResources,
            ],
            'resourceSectionId',
          ),
        }
      : null;

    if (payload) {
      dispatch(updateDocResource(payload));
    }

    executeUpdateResourceSectionCommand(
      {
        resourceSectionId,
        resourceIds: updatedResourceIds,
      },
      editor,
    );
    setUpdatedResources([]);
  };

  useEffect(() => {
    let filteredIds = [] as number[];

    if (resourceSectionType === ResourceSectionType.Approval) {
      const approvalEntries = highlightTasks?.reduce((acc, task) => {
        return [...acc, ...(task.approvals || [])];
      }, [] as Approval[]);

      filteredIds = map(
        approvalEntries?.filter((approval) =>
          isTaskApprovalMatchesFilter(
            resourceSectionFilterType,
            resourceSectionFilterRange,
            approval,
          ),
        ),
        'approvalId',
      );
    } else {
      filteredIds = map(
        highlightTasks?.filter((task) =>
          isTaskMatchesFilter(
            resourceSectionFilterType,
            resourceSectionFilterRange,
            task,
            statusField,
          ),
        ),
        'taskId',
      );
    }

    if (!isEqual(filteredIds.sort(), originalResourceIds?.sort())) {
      setUpdatedResources(filteredIds);
    } else {
      setUpdatedResources([]);
    }
  }, [
    highlightTasks,
    resourceSectionType,
    resourceSectionFilterType,
    originalResourceIds,
    resourceSectionFilterRange,
    statusField,
  ]);

  const getStatusIcon = useCallback(() => {
    switch (resourceSectionType) {
      case ResourceSectionType.Task:
        return <ProgressPie progress={0} type="disabled" />;
      case ResourceSectionType.Milestone:
        return <MilestoneIcon />;
      case ResourceSectionType.Approval:
        return <ApproveOutline16 />;
      default:
        return <ProgressPie progress={0} type="disabled" />;
    }
  }, [resourceSectionType]);

  const confirmPublish = () => {
    return showConfirmPopup(
      {
        title: updateConfirmationMessageTitle,
        content: updateConfirmationMessageContent,
        ok: translatedYesText,
        cancel: translatedNoText,
      },
      onUpdateEntries,
    );
  };

  const isResponsive = useIsResponsive();

  return (
    <Container>
      <Title isResponsive={isResponsive}>
        <TitleMain>
          <StatusIcon data-resource-list-el-type="status-icon">
            {getStatusIcon()}
          </StatusIcon>
          <ResourceText>
            <FormattedMessage id={getResourceType(resourceSectionType)} />
          </ResourceText>
          <ResourceText>
            <FormattedMessage id={resourceSectionFilterType} />
          </ResourceText>
        </TitleMain>
        {![TaskStatusType.Blocked, TaskStatusType.InProgress].includes(
          resourceSectionFilterType,
        ) ? (
          <RangeInfo>
            {' '}
            •{' '}
            <FormattedMessage
              id="range.info"
              values={{
                start: (
                  <HumanisedDate
                    timestamp={moment(resourceSectionFilterRange[0])}
                  />
                ),
                end: (
                  <HumanisedDate
                    timestamp={moment(resourceSectionFilterRange[1])}
                  />
                ),
              }}
            />
          </RangeInfo>
        ) : null}
      </Title>
      {updatedResourceIds.length > 1 ? (
        <TertiaryButton
          className="resource_list__update_btn"
          data-email-template-ignore
          size="small"
          onClick={confirmPublish}
          type="default"
          iconPlacement="right"
        >
          <FormattedMessage id="update" />
        </TertiaryButton>
      ) : null}
    </Container>
  );
};
